import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { useMediaSettingsStore, useRoomActionsStore, useVideoStreamStore } from '@src/stores';

import { LocalStorage } from '@src/services';

import { stopTracks, findNewDevices, devicesAreEqual, isWebView, isMacOS } from '@src/helpers';
import { debounce, isSamsungDevice } from '@src/utils';
import mediaDevices from '@src/services/mediaDevices';

const useEnumerateDevices = () => {
  const prevDevicesRef = useRef<MediaDeviceInfo[]>([]);
  const deviceChangeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const nestedDeviceChangeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isSamsungRef = useRef<boolean>(isSamsungDevice());
  // Ensure any "Bluetooth preference" logic only runs during the very first initialization phase
  // and never overrides runtime device-change selection or explicit user choices.
  const hasAppliedInitialAudioPreferenceRef = useRef(false);

  const {
    isAudioAllowed,
    isVideoAllowed,
    speakerDevices,
    videoInputDevices,
    audioInputDevices,
    setMediaSettings,
    setIsEnumerateAudioDevices,
    setIsEnumerateVideoDevices,
  } = useMediaSettingsStore(
    useShallow((state) => ({
      isVideoAllowed: state.mediaSettings.isVideoAllowed,
      isAudioAllowed: state.mediaSettings.isAudioAllowed,
      speakerDevices: state.mediaSettings.speakerDevices,
      audioInputDevices: state.mediaSettings.audioInputDevices,
      videoInputDevices: state.mediaSettings.videoInputDevices,
      setMediaSettings: state.setMediaSettings,
      setIsEnumerateAudioDevices: state.setIsEnumerateAudioDevices,
      setIsEnumerateVideoDevices: state.setIsEnumerateVideoDevices,
    }))
  );

  const { setOriginalStream } = useVideoStreamStore(
    useShallow((state) => ({
      setOriginalStream: state.setOriginalStream,
    }))
  );

  const { isCamClosed, setCamSettings } = useRoomActionsStore(
    useShallow((state) => ({
      isCamClosed: state.camSettings.isCamClosed,
      setCamSettings: state.setCamSettings,
    }))
  );

  const localStorageInstance = useMemo(() => new LocalStorage(), []);

  const findBluetoothDevice = useCallback((devices: MediaDeviceInfo[]) => {
    return devices.find((d) => (d?.label || '').toLowerCase().includes('bluetooth'));
  }, []);

  // ####################################################
  // ENUMERATE DEVICES
  // ####################################################

  const enumerateVideoDevices = useCallback(
    (stream: MediaStream) => {
      const isSamsung = isSamsungRef.current;
      const enumeratePromise = isSamsung ? mediaDevices.enumerateDevices() : navigator.mediaDevices.enumerateDevices();

      enumeratePromise
        .then((devices) => {
          prevDevicesRef.current = devices;
          const videoDevices = devices.filter(({ kind }) => kind === 'videoinput');
          const isMobile = /Mobi|Android/i.test(navigator.userAgent);

          let processedVideoDevices = [...videoDevices];

          // For mobile devices, ensure we have clear front/back camera options
          if (isMobile) {
            // Create clear front/back camera options for mobile
            const frontCamera = {
              deviceId: 'mobile-front',
              groupId: 'mobile-cameras',
              kind: 'videoinput',
              label: 'Front Camera',
              toJSON: () => ({ deviceId: 'mobile-front', kind: 'videoinput', label: 'Front Camera' }),
            } as MediaDeviceInfo;

            const backCamera = {
              deviceId: 'mobile-back',
              groupId: 'mobile-cameras',
              kind: 'videoinput',
              label: 'Back Camera',
              toJSON: () => ({ deviceId: 'mobile-back', kind: 'videoinput', label: 'Back Camera' }),
            } as MediaDeviceInfo;

            // Use mobile-specific camera options
            processedVideoDevices = [frontCamera, backCamera];
          }

          const { video = {} } = localStorageInstance.getLocalStorageDevices() || {};

          const selectedCamera = processedVideoDevices.find(({ deviceId }) => video.select === deviceId);

          setMediaSettings({
            videoInputDevices: [...processedVideoDevices],
            ...(processedVideoDevices.length && {
              selectedVideoInputDevice: selectedCamera || processedVideoDevices[0],
            }),
          });
        })
        .then(() => {
          setIsEnumerateVideoDevices(true);
          setMediaSettings({ isVideoAllowed: true });
          stopTracks(stream);
        })
        .catch((error) => {
          setIsEnumerateVideoDevices(false);
          setMediaSettings({ isVideoAllowed: false });
          console.warn('enumerateVideoDevices', { error });
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [localStorageInstance]
  );
  const enumerateAudioDevices = useCallback(
    async (stream: MediaStream) => {
      const isSamsung = isSamsungRef.current;
      const enumeratePromise = isSamsung ? mediaDevices.enumerateDevices() : navigator.mediaDevices.enumerateDevices();

      try {
        const devices = await enumeratePromise;
        prevDevicesRef.current = devices;
        const { audio = {}, speaker = {} } = localStorageInstance.getLocalStorageDevices() || {};
        const audioInDevices = devices.filter(({ kind }) => kind === 'audioinput');
        const audioOutDevices = devices.filter(({ kind }) => kind === 'audiooutput');
        const isMobileUa = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');

        // Prefer stored selection; otherwise prefer browser logical devices:
        // - "communications" (best for calls / headsets on Windows/Chrome)
        // - then "default"
        // This avoids hard-pinning a potentially stale first enumerated device on first join.
        const storedAudio = audioInDevices.find(({ deviceId }) => audio.select === deviceId);
        const storedSpeaker = audioOutDevices.find(({ deviceId }) => speaker.select === deviceId);
        const communicationsAudio = audioInDevices.find(({ deviceId }) => deviceId === 'communications');
        const communicationsSpeaker = audioOutDevices.find(({ deviceId }) => deviceId === 'communications');
        const defaultAudio = audioInDevices.find(({ deviceId }) => deviceId === 'default');
        const defaultSpeaker = audioOutDevices.find(({ deviceId }) => deviceId === 'default');

        // If we have a stored selection but it's not present anymore (common after BT reconnect),
        // treat it as stale and prefer Bluetooth if available.
        const isStoredAudioStale = !!audio?.select && !storedAudio;
        const isStoredSpeakerStale = !!speaker?.select && !storedSpeaker;

        // Initialization-only mobile bugfix: on some mobile browsers the "default"/"communications" logical devices
        // are missing. If a Bluetooth headset is connected, prefer it over an arbitrary first
        // physical device so the very first join routes audio to the headset.
        const shouldApplyInitialBtPreference =
          isMobileUa &&
          !hasAppliedInitialAudioPreferenceRef.current &&
          ((!audio?.select && !speaker?.select) || isStoredAudioStale || isStoredSpeakerStale);

        const bluetoothAudio = shouldApplyInitialBtPreference ? findBluetoothDevice(audioInDevices) : undefined;
        const bluetoothSpeaker = shouldApplyInitialBtPreference ? findBluetoothDevice(audioOutDevices) : undefined;

        const selectedAudio = storedAudio || bluetoothAudio || communicationsAudio || defaultAudio || audioInDevices[0];
        const selectedSpeaker =
          storedSpeaker || bluetoothSpeaker || communicationsSpeaker || defaultSpeaker || audioOutDevices[0];

        // Persist the Bluetooth choice immediately (not only on leave) but ONLY during first-time initialization.
        if (shouldApplyInitialBtPreference) {
          if (bluetoothAudio?.deviceId) {
            localStorageInstance.setLocalStorageDevices(localStorageInstance.MEDIA_TYPE.audio, bluetoothAudio.deviceId);
          }
          if (bluetoothSpeaker?.deviceId) {
            localStorageInstance.setLocalStorageDevices(
              localStorageInstance.MEDIA_TYPE.speaker,
              bluetoothSpeaker.deviceId
            );
          }
          hasAppliedInitialAudioPreferenceRef.current = true;
        }

        setMediaSettings({
          audioInputDevices: [...audioInDevices],
          speakerDevices: [...audioOutDevices],
          ...(audioOutDevices.length && { selectedSpeakerDevice: selectedSpeaker || audioOutDevices[0] }),
          ...(audioInDevices.length && { selectedAudioInputDevice: selectedAudio || audioInDevices[0] }),
        });

        setIsEnumerateAudioDevices(true);
        setMediaSettings({ isAudioAllowed: true });
        stopTracks(stream);
      } catch (error) {
        setIsEnumerateAudioDevices(false);
        setMediaSettings({ isAudioAllowed: false });
        console.log('enumerateAudioDevices', { error });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [findBluetoothDevice, localStorageInstance]
  );

  // ####################################################
  // ####################################################
  // INITIALIZE DEVICE ENUMERATION

  const initEnumerateVideoDevices = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setMediaSettings({ isVideoAllowed: false });
      return;
    }

    const processSilentDevices = (devices: MediaDeviceInfo[]) => {
      const videoDevices = devices.filter(({ kind }) => kind === 'videoinput');
      const hasLabels = videoDevices.some((d) => !!d.label);

      // Only skip getUserMedia if we have labels (proof of prior permission grant).
      // Without labels, we MUST request permission via getUserMedia.
      if (!hasLabels) {
        return false; // Need getUserMedia to request permission
      }

      // We have labels - permission was granted before, process silently
      const isMobile = /Mobi|Android/i.test(navigator.userAgent);
      let processedVideoDevices = [...videoDevices];

      if (isMobile) {
        processedVideoDevices = [
          {
            deviceId: 'mobile-front',
            groupId: 'mobile-cameras',
            kind: 'videoinput',
            label: 'Front Camera',
            toJSON: () => ({ deviceId: 'mobile-front', kind: 'videoinput', label: 'Front Camera' }),
          } as MediaDeviceInfo,
          {
            deviceId: 'mobile-back',
            groupId: 'mobile-cameras',
            kind: 'videoinput',
            label: 'Back Camera',
            toJSON: () => ({ deviceId: 'mobile-back', kind: 'videoinput', label: 'Back Camera' }),
          } as MediaDeviceInfo,
        ];
      }

      const { video = {} } = localStorageInstance.getLocalStorageDevices() || {};
      const selectedCamera = processedVideoDevices.find(({ deviceId }) => video.select === deviceId);

      setMediaSettings({
        videoInputDevices: [...processedVideoDevices],
        ...(processedVideoDevices.length && {
          selectedVideoInputDevice: selectedCamera || processedVideoDevices[0],
        }),
        isVideoAllowed: true,
      });

      setIsEnumerateVideoDevices(true);
      return true; // Handled silently
    };

    const isSamsung = isSamsungRef.current;

    // 1. Try silent enumeration first
    try {
      const enumeratePromise = isSamsung ? mediaDevices.enumerateDevices() : navigator.mediaDevices.enumerateDevices();

      const devices = await enumeratePromise;
      if (processSilentDevices(devices)) {
        return; // Success, no light needed
      }
    } catch (e) {
      console.warn('Silent enumeration failed', e);
    }

    // 2. Fallback to getUserMedia (turns on light)
    const getUserMediaPromise = isSamsung
      ? mediaDevices.getUserMedia({ video: true })
      : navigator.mediaDevices.getUserMedia({ video: true });

    getUserMediaPromise
      .then((stream) => {
        enumerateVideoDevices(stream); // 2️- If granted, fetch and store the list of cameras
      })
      .catch(async (err) => {
        console.warn('Video permission denied', err);

        // Fallback: still enumerate devices without labels (again)
        const enumeratePromise = isSamsung
          ? mediaDevices.enumerateDevices()
          : navigator.mediaDevices.enumerateDevices();

        const devices = await enumeratePromise;
        const videoDevices = devices.filter(({ kind }) => kind === 'videoinput');

        setMediaSettings({
          videoInputDevices: [...videoDevices],
          ...(videoDevices.length && { selectedVideoInputDevice: videoDevices[0] }),
        });

        setIsEnumerateVideoDevices(true);
        // turn off the state of camera
        setCamSettings({ isCamClosed: true });
      });
  }, [enumerateVideoDevices, localStorageInstance, setCamSettings, setIsEnumerateVideoDevices, setMediaSettings]);

  const initEnumerateAudioDevices = useCallback(() => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setMediaSettings({ isAudioAllowed: false });
      return Promise.resolve(false);
    }

    const isSamsung = isSamsungRef.current;
    const getUserMediaPromise = isSamsung
      ? mediaDevices.getUserMedia({ audio: true })
      : navigator.mediaDevices.getUserMedia({ audio: true });

    return getUserMediaPromise
      .then(async (stream) => {
        await enumerateAudioDevices(stream);
        return true;
      })
      .catch((_err) => {
        setMediaSettings({ isAudioAllowed: false });
        return false;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enumerateAudioDevices]);

  const initEnumerateDevices = useCallback(() => {
    initEnumerateAudioDevices();
    initEnumerateVideoDevices();
  }, [initEnumerateAudioDevices, initEnumerateVideoDevices]);

  // ####################################################
  // ####################################################

  const refreshMyAudioDevices = useCallback(() => {
    if (!isAudioAllowed) return;
    initEnumerateAudioDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioInputDevices, initEnumerateAudioDevices, isAudioAllowed, speakerDevices]);

  const refreshMyVideoDevices = useCallback(() => {
    if (!isVideoAllowed) return;
    initEnumerateVideoDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initEnumerateVideoDevices, isVideoAllowed, videoInputDevices]);

  const refreshMyAudioVideoDevices = useCallback(() => {
    refreshMyVideoDevices();
    refreshMyAudioDevices();
  }, [refreshMyAudioDevices, refreshMyVideoDevices]);

  useEffect(() => {
    refreshMyAudioVideoDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle device changes with debounce
  const debouncedHandleDeviceChange = useMemo(
    () =>
      debounce(
        async (devices?: MediaDeviceInfo[]) => {
          try {
            // Use media-devices package for Samsung devices for better reliability
            const isSamsung = isSamsungRef.current;
            let newDevices: MediaDeviceInfo[];

            if (isSamsung) {
              try {
                // The media-devices package provides more reliable device enumeration
                newDevices = await mediaDevices.enumerateDevices();
              } catch (error) {
                newDevices = await navigator.mediaDevices.enumerateDevices();
              }
            } else {
              newDevices = await navigator.mediaDevices.enumerateDevices();
            }

            if (!devicesAreEqual(prevDevicesRef.current, newDevices)) {
              // Find newly added devices
              const addedDevices = findNewDevices(prevDevicesRef.current, newDevices);

              const audioInDevices = newDevices.filter(({ kind }) => kind === 'audioinput');
              const audioOutDevices = newDevices.filter(({ kind }) => kind === 'audiooutput');
              // VIDEO DEVICES
              const videoInDevices = newDevices.filter(({ kind }) => kind === 'videoinput');

              // Get stored preferences
              const { audio = {}, speaker = {}, video = {} } = localStorageInstance.getLocalStorageDevices() || {};

              // Check for newly added devices
              const newAudioInputDevice = addedDevices.find((device) => device.kind === 'audioinput');
              const newAudioOutputDevice = addedDevices.find((device) => device.kind === 'audiooutput');
              // VIDEO: Check for newly added video device
              const newVideoInputDevice = addedDevices.find((device) => device.kind === 'videoinput');

              // Determine the audio input device to use
              let newSelectedAudio;
              const storedAudioDevice = audioInDevices.find(({ deviceId }) => audio.select === deviceId);
              const communicationsAudioDevice = audioInDevices.find(({ deviceId }) => deviceId === 'communications');
              const defaultAudioDevice = audioInDevices.find(({ deviceId }) => deviceId === 'default');
              const bluetoothAudioDevice = findBluetoothDevice(audioInDevices);
              const ua = navigator.userAgent || '';
              const isMobileUa = /Mobi|Android|iPhone|iPad|iPod/i.test(ua);

              // If a new audio input device is plugged in, prefer it
              if (newAudioInputDevice) {
                newSelectedAudio = newAudioInputDevice;
              }
              // Otherwise use stored preference if available
              else if (storedAudioDevice) {
                newSelectedAudio = storedAudioDevice;
              }
              // Mobile/Samsung: If Bluetooth is present and stored selection is missing/stale, prefer Bluetooth
              else if ((isSamsung || isMobileUa) && bluetoothAudioDevice) {
                newSelectedAudio = bluetoothAudioDevice;
              }
              // Otherwise use "communications" device when available (ideal for meetings)
              else if (communicationsAudioDevice) {
                newSelectedAudio = communicationsAudioDevice;
              }
              // Otherwise use "default" device when available
              else if (defaultAudioDevice) {
                newSelectedAudio = defaultAudioDevice;
              }
              // Finally fall back to the first available device
              else {
                newSelectedAudio = audioInDevices[0];
              }

              // Determine the speaker device to use
              let newSelectedSpeaker;
              const storedSpeakerDevice = audioOutDevices.find(({ deviceId }) => speaker.select === deviceId);
              const communicationsSpeakerDevice = audioOutDevices.find(({ deviceId }) => deviceId === 'communications');
              const defaultSpeakerDevice = audioOutDevices.find(({ deviceId }) => deviceId === 'default');
              const bluetoothSpeakerDevice = findBluetoothDevice(audioOutDevices);

              // If a new audio output device is plugged in, prefer it
              if (newAudioOutputDevice) {
                newSelectedSpeaker = newAudioOutputDevice;
              }
              // Otherwise use stored preference if available
              else if (storedSpeakerDevice) {
                newSelectedSpeaker = storedSpeakerDevice;
              }
              // Mobile/Samsung: If Bluetooth is present and stored selection is missing/stale, prefer Bluetooth
              else if ((isSamsung || isMobileUa) && bluetoothSpeakerDevice) {
                newSelectedSpeaker = bluetoothSpeakerDevice;
              }
              // Otherwise use "communications" device when available (ideal for meetings)
              else if (communicationsSpeakerDevice) {
                newSelectedSpeaker = communicationsSpeakerDevice;
              }
              // Otherwise use "default" device when available
              else if (defaultSpeakerDevice) {
                newSelectedSpeaker = defaultSpeakerDevice;
              }
              // Finally fall back to the first available device
              else {
                newSelectedSpeaker = audioOutDevices[0];
              }

              // VIDEO: Determine the video input device to use
              let newSelectedVideo;
              const storedVideoDevice = videoInDevices.find(({ deviceId }) => video.select === deviceId);
              if (newVideoInputDevice) {
                newSelectedVideo = newVideoInputDevice;
              } else if (storedVideoDevice) {
                newSelectedVideo = storedVideoDevice;
              } else {
                newSelectedVideo = videoInDevices[0];
              }

              // Update the media settings
              setMediaSettings({
                audioInputDevices: [...audioInDevices],
                speakerDevices: [...audioOutDevices],
                videoInputDevices: [...videoInDevices],
                ...(audioOutDevices.length && { selectedSpeakerDevice: newSelectedSpeaker }),
                ...(audioInDevices.length && { selectedAudioInputDevice: newSelectedAudio }),
                ...(videoInDevices.length && { selectedVideoInputDevice: newSelectedVideo }),
              });

              // Persist the new effective choices so any later re-enumeration
              // (e.g. when opening Settings or toggling mic) uses the same devices.
              // Samsung-specific: only Samsung gets the new audio/speaker persistence logic.
              if (isSamsung) {
                if (newSelectedAudio?.deviceId) {
                  localStorageInstance.setLocalStorageDevices('audio', newSelectedAudio.deviceId);
                }
                if (newSelectedSpeaker?.deviceId) {
                  localStorageInstance.setLocalStorageDevices('speaker', newSelectedSpeaker.deviceId);
                }
              }
              if (newSelectedVideo?.deviceId) {
                localStorageInstance.setLocalStorageDevices('video', newSelectedVideo.deviceId);
              }

              if (isSamsung) {
                // Samsung-specific: More aggressive device switching
                if (newVideoInputDevice && isVideoAllowed && !isCamClosed) {
                  try {
                    // Use media-devices for Samsung to get the stream
                    const videoStream = await mediaDevices.getUserMedia({
                      video: {
                        deviceId: { exact: newVideoInputDevice.deviceId },
                      },
                    });
                    setOriginalStream(videoStream);
                    // Also trigger camera change through RoomClient if in room
                    const roomClient = (window as any).roomClient;
                    if (roomClient?.atomicCloseThenProduce) {
                      roomClient.atomicCloseThenProduce('videoType', newVideoInputDevice.deviceId);
                    }
                  } catch (e) {
                    try {
                      const videoStream = await navigator.mediaDevices.getUserMedia({
                        video: { deviceId: { exact: newVideoInputDevice.deviceId } },
                      });
                      setOriginalStream(videoStream);
                    } catch (fallbackError) {
                      console.error('❌ Fallback also failed:', fallbackError);
                    }
                  }
                }
                // Samsung: Use media-devices for ALL video operations when there is no brand-new device
                else if (newSelectedVideo && isVideoAllowed && !isCamClosed) {
                  try {
                    const videoStream = await mediaDevices.getUserMedia({
                      video: { deviceId: newSelectedVideo.deviceId },
                    });
                    setOriginalStream(videoStream);
                  } catch (e) {
                    console.error('Failed to get video stream for device', e);
                  }
                }

                // Samsung-specific: If a NEW audio input device appears while the mic is currently open,
                // trigger the same onMicClick pipeline that Settings uses, but without
                // toggling the UI state (we call onMicClick(false) when already unmuted).
                if (newAudioInputDevice && isAudioAllowed) {
                  const { micSettings } = useRoomActionsStore.getState();
                  const { isMicClosed, onMicClick } = micSettings || {};

                  if (!isMicClosed && typeof onMicClick === 'function') {
                    try {
                      // This will run the onMicClickCallback from useMeetRoom,
                      // which does the close/resume/produce logic against RoomClient.
                      onMicClick(false);
                    } catch (err) {
                      console.error('❌ Failed to refresh mic on device change', err);
                    }
                  }
                }
              } else {
                // Non-Samsung: preserve original behaviour
                // If we have a new video device and permissions, update the video stream
                if (newSelectedVideo && isVideoAllowed && !isCamClosed) {
                  try {
                    const videoStream = await navigator.mediaDevices.getUserMedia({
                      video: { deviceId: newSelectedVideo.deviceId },
                    });
                    setOriginalStream(videoStream);
                  } catch (e) {
                    console.error('Failed to get video stream for new device', e);
                  }
                }

                // If we have a new audio device and permissions, update the audio stream
                if (newSelectedAudio && isAudioAllowed) {
                  try {
                    // If the mic is currently open in the meeting, also refresh the audio producer
                    // so the new/default input is actually used (not just reflected in originalStream).
                    const { micSettings } = useRoomActionsStore.getState();
                    const { isMicClosed, onMicClick, onMicClickCallback } = micSettings || {};
                    if (!isMicClosed && typeof onMicClick === 'function' && typeof onMicClickCallback === 'function') {
                      try {
                        onMicClick(false);
                      } catch (err) {
                        console.error('❌ Failed to refresh mic on device change (non-Samsung)', err);
                      }
                    }
                  } catch (e) {
                    console.error('Failed to get audio stream for new device', e);
                  }
                }
              }

              // Update the reference to current devices
              prevDevicesRef.current = newDevices;
            }
          } catch (error) {
            console.error('Error handling device change:', error);
          }
        },
        isSamsungRef.current ? 100 : 300
      ), // Faster response for Samsung devices
    [
      localStorageInstance,
      findBluetoothDevice,
      setMediaSettings,
      isVideoAllowed,
      isCamClosed,
      isAudioAllowed,
      setOriginalStream,
    ]
  );

  const handleDeviceChange = useCallback(() => {
    const isSamsung = isSamsungRef.current;

    if (isSamsung) {
      // Mirror Settings behaviour: re-run full audio/video enumeration on Samsung.
      // These helpers already use the media-devices package under the hood,
      // so this keeps us fully dependent on the new library while reusing
      // the same, well-tested selection logic as the Settings panel.
      initEnumerateAudioDevices();
      initEnumerateVideoDevices();
    }

    // IMPORTANT:
    // On Samsung, initEnumerateAudioDevices/initEnumerateVideoDevices already re-enumerate and update selection.
    // Calling the diff-based handler immediately can race and overwrite the selection (e.g. Bluetooth -> default).
    // So: run immediate debounce only for non-Samsung; Samsung still gets delayed checks below.
    if (!isSamsung) {
      debouncedHandleDeviceChange();
    }

    // On Android browsers, some Bluetooth devices appear with a slight delay
    // after the initial devicechange event. Schedule a second, slower check
    // ~4s later to catch late device registrations and improve stability.
    const ua = navigator.userAgent || '';
    const isAndroid = /Android/i.test(ua);
    const isAndroidBrowser = isAndroid && !isWebView();

    // Enhanced handling for Samsung devices
    // Samsung devices often have more delayed device registration
    if (isSamsung || isAndroidBrowser) {
      if (deviceChangeTimeoutRef.current) {
        clearTimeout(deviceChangeTimeoutRef.current);
      }
      if (nestedDeviceChangeTimeoutRef.current) {
        clearTimeout(nestedDeviceChangeTimeoutRef.current);
      }

      // Use multiple delayed checks for Samsung devices for better reliability
      const delayMs = isSamsung ? 5000 : 4000;

      deviceChangeTimeoutRef.current = setTimeout(() => {
        debouncedHandleDeviceChange();

        // For Samsung, add one more check after 2 more seconds
        if (isSamsung) {
          nestedDeviceChangeTimeoutRef.current = setTimeout(() => {
            debouncedHandleDeviceChange();
          }, 2000);
        }
      }, delayMs);
    }
  }, [debouncedHandleDeviceChange, initEnumerateAudioDevices, initEnumerateVideoDevices]);

  useEffect(() => {
    // Initial device enumeration
    const isSamsung = isSamsungRef.current;

    const enumerateInitialDevices = isSamsung
      ? mediaDevices.enumerateDevices()
      : navigator.mediaDevices.enumerateDevices();

    enumerateInitialDevices
      .then((devices) => {
        prevDevicesRef.current = devices;
        // Trigger initial device setup
        handleDeviceChange();
      })
      .catch((error) => {
        console.error('Initial device enumeration failed:', error);
        // Fallback to standard API if media-devices fails
        if (isSamsung) {
          navigator.mediaDevices
            .enumerateDevices()
            .then((devices) => {
              prevDevicesRef.current = devices;
              handleDeviceChange();
            })
            .catch(console.error);
        }
      });

    // Add device change listener
    // Note: media-devices package doesn't have its own event system,
    // so we use native listener but media-devices for enumeration/getUserMedia
    navigator.mediaDevices?.addEventListener('devicechange', handleDeviceChange);

    return () => {
      navigator.mediaDevices?.removeEventListener('devicechange', handleDeviceChange);
      if (deviceChangeTimeoutRef.current) {
        clearTimeout(deviceChangeTimeoutRef.current);
      }
      if (nestedDeviceChangeTimeoutRef.current) {
        clearTimeout(nestedDeviceChangeTimeoutRef.current);
      }
    };
  }, [handleDeviceChange]);

  // MacOS Helper: Poll for device changes since devicechange event is unreliable on some Mac browsers
  // Use a ref for the callback to prevent the effect from re-running constantly
  const handleDeviceChangeRef = useRef(handleDeviceChange);
  useEffect(() => {
    handleDeviceChangeRef.current = handleDeviceChange;
  }, [handleDeviceChange]);

  useEffect(() => {
    if (!isMacOS()) return;

    const intervalId = setInterval(() => {
      // Call the latest version of the function
      if (handleDeviceChangeRef.current) {
        handleDeviceChangeRef.current();
      }
    }, 5000);

    return () => clearInterval(intervalId);
  }, []);

  return {
    initEnumerateDevices,
    refreshMyAudioDevices,
    refreshMyVideoDevices,
    initEnumerateAudioDevices,
    initEnumerateVideoDevices,
    refreshMyAudioVideoDevices,
  };
};

export default useEnumerateDevices;
